/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * SoonKi Min (skmin@rsupport.com)
 *
 * @description
 *
 * @created at Wed Jan 31 2024
 **/

import { Theme } from "@emotion/react";
import styled from "@emotion/styled";
import { useState } from "react";

import { useChatMessageActions } from "@/frameworks/core/chat";
import { Row, Tooltip, Typography, TypographyCSS } from "@/frameworks/designStuff/components";
import { useMouseEnterWait } from "@/frameworks/libraries/hooks";
import { Configure } from "@/frameworks/libraries/utilities";
import { _T } from "@rfice/language";
import EmojiData from "@/frameworks/core/components/Chat/Emoji/emoji.json";

const ReactionMap = {
	SMILE: "😊",
	HEART: "❤️",
	FIRE: "🔥",
	THUMB: "👍",
	HUC: "😱",
	CRY: "😢"
};

type Reaction = WebServices.Chat.Messages.Data["content"][number]["reactions"][number];

export default function ChatReactionList({
	reactions,
	messageID,
	roomID
}: {
	reactions: Reaction[];
	messageID: string;
	roomID: string;
}) {
	const { addReaction, removeReaction } = useChatMessageActions();
	const [isLoading, setIsLoadning] = useState(false);

	const handleReaction = async (reaction: Reaction) => {
		const targetReaction = reactions.find((v) => v.type === reaction.type);
		setIsLoadning(true);
		if (targetReaction && targetReaction.users.find((user) => user.id === Configure.userID)) {
			await removeReaction({
				messageId: messageID,
				roomId: roomID,
				type: reaction.type
			});
		} else {
			await addReaction({
				messageId: messageID,
				roomId: roomID,
				type: reaction.type
			});
		}
		setIsLoadning(false);
	};

	const sortReactionsByUser = (reactions: Reaction[]): Reaction[] => {
		const foundMe = reactions.filter((reaction) => reaction.users.some((user) => user.id === Configure.userID));
		const rest = reactions.filter((reaction) => !reaction.users.some((user) => user.id === Configure.userID));

		return [...foundMe, ...rest];
	};

	return (
		<Row css={{ paddingLeft: "38px", paddingTop: "4px", flexWrap: "wrap" }} gap={4}>
			{sortReactionsByUser(reactions).map((reaction) => {
				return (
					<ReactionButton
						key={reaction.type}
						reaction={reaction}
						onReaction={handleReaction}
						isLoading={isLoading}
					/>
				);
			})}
		</Row>
	);
}

type PrevReaction = "SMILE" | "HEART" | "FIRE" | "THUMB" | "HUC" | "CRY";

const ReactionButton = ({
	reaction,
	onReaction,
	isLoading
}: {
	reaction: Reaction;
	onReaction: (reaction: Reaction) => void;
	isLoading: boolean;
}) => {
	const [showTooltip, setShowTooltip] = useState(false);
	const { ref } = useMouseEnterWait(() => {
		setShowTooltip(true);
	}, 500);

	const mouseLeave = () => {
		setShowTooltip(false);
	};

	const sortedUser = (data: SwaggerModels.ChatReactionUserResponseVO[]) => {
		const foundMe = data.find((item) => item.id === Configure.userID);

		if (foundMe) {
			const rest = data.filter((item) => item.id !== Configure.userID);

			return [{ id: foundMe.id, name: _T("나") }, ...rest];
		} else {
			return data;
		}
	};

	return (
		<StyledReactionButton
			ref={ref}
			id={reaction.type}
			typoColor={reaction.users.find((user) => user.id === Configure.userID) ? "accent_40_100" : "neutral_80_100"}
			onClick={() => {
				onReaction(reaction);
			}}
			onMouseLeave={mouseLeave}
			disabled={isLoading}
		>
			<div style={{ padding: "1px 8px" }}>
				<Tooltip
					isShow={showTooltip}
					hasIcon={false}
					message={
						<Typography maxLine={3} ellipsis css={{ maxWidth: "200px" }}>
							{sortedUser(reaction.users)
								.map((user) => user.name)
								.join(", ")}
						</Typography>
					}
				>
					<div className="chat-font">
						{ReactionMap[reaction.type as PrevReaction]
							? `${ReactionMap[reaction.type as PrevReaction]} ${reaction.count}`
							: EmojiData.flatMap((category) =>
									category?.emojis?.filter((emoji) => emoji.slug === reaction.type)
								)[0].emoji +
								" " +
								reaction.count}
					</div>
				</Tooltip>
			</div>
		</StyledReactionButton>
	);
};

const StyledReactionButton = styled(Row.button)<{ typoColor: keyof Theme["colors"] }>`
	border: 1px solid ${({ theme }) => theme.colors.neutral_20_100};
	background: ${({ theme }) => theme.colors.neutral_00_100};
	border-radius: 16px;
	height: 26px;
	${({ typoColor }) => TypographyCSS("Body2", typoColor)};
	cursor: pointer;
	&:hover {
		background: ${({ theme }) => theme.colors.secondary_80_8};
	}
`;
